import { consoleEvents, getLabelFromStorage, LoadingStates } from '@pb/utils'
import { createReducer } from 'redux-act'

import { Article } from '../components/OrderDetailsPage/StyleYourLatestCarousel/types'
import actions from './actions'
import { ONLINE_ORDERS_LOAD_MORE_AMOUNT, STORE_ORDERS_LOAD_MORE_AMOUNT } from './constants'
import initialState from './initialState'
import { PurchasesState } from './types'

// ----------------------------------------
// FETCH ONLINE ORDERS
// ----------------------------------------
const fetchOnlineOrdersStart = (state): PurchasesState => ({
  ...state,
  isOnlineOrdersLoading: true,
})

const fetchOnlineOrdersSuccess = (state, onlineOrders): PurchasesState => ({
  ...state,
  onlineOrders: [...state.onlineOrders, ...onlineOrders],
  isOnlineOrdersLoading: false,
  isOnlineOrdersPopulated: true,
  fetchOnlineIndex: {
    start: state.fetchOnlineIndex.start + ONLINE_ORDERS_LOAD_MORE_AMOUNT,
    end: state.fetchOnlineIndex.end + ONLINE_ORDERS_LOAD_MORE_AMOUNT,
  },
})

const fetchOnlineOrdersFail = (state, error): PurchasesState => {
  consoleEvents.error('Failed to fetch online orders', error)
  return {
    ...state,
    isOnlineOrdersLoading: false,
    isOnlineOrdersLoadingFailed: true,
    isHubOrderListPopulated: state.isStoreOrdersLoadingFailed,
  }
}

// ----------------------------------------
// Style your latest / articleDetails
// ----------------------------------------

const saveSYLArticles = (state, articles: Article[]): PurchasesState => ({
  ...state,
  styleYourLatest: {
    ...state.styleYourLatest,
    articles,
  },
})

const setUserHasOrderRecommendations = (
  state,
  userHasOrderRecommendations = false,
): PurchasesState => ({
  ...state,
  styleYourLatest: {
    ...state.styleYourLatest,
    userHasOrderRecommendations,
  },
})

// ----------------------------------------
// FETCH ONLINE ACTIVE ORDERS
// ----------------------------------------
const fetchOnlineActiveOrdersStart = (state): PurchasesState => ({
  ...state,
  onlineActiveOrdersLoadingState: LoadingStates.Loading,
})

const fetchOnlineActiveOrdersSuccess = (state, orders): PurchasesState => ({
  ...state,
  onlineActiveOrders: [...state.onlineActiveOrders, ...orders],
  onlineActiveOrdersLoadingState: LoadingStates.LoadedSuccess,
  fetchOnlineActiveIndex: {
    start: state.fetchOnlineActiveIndex.start + ONLINE_ORDERS_LOAD_MORE_AMOUNT,
    end: state.fetchOnlineActiveIndex.end + ONLINE_ORDERS_LOAD_MORE_AMOUNT,
  },
})

const fetchOnlineActiveOrdersFail = (state, error): PurchasesState => {
  consoleEvents.error('Failed to fetch online active orders', error)
  return {
    ...state,
    onlineActiveOrdersLoadingState: LoadingStates.LoadedFail,
  }
}

// ----------------------------------------
// CANCEL ONLINE ORDER
// ----------------------------------------
const cancelOnlineOrderStart = (state): PurchasesState => ({
  ...state,
  cancelOnlineOrderLoadingState: LoadingStates.Loading,
})

const cancelOnlineOrderSuccess = (state): PurchasesState => ({
  ...state,
  cancelOnlineOrderLoadingState: LoadingStates.LoadedSuccess,
})

const cancelOnlineOrderFail = (state): PurchasesState => ({
  ...state,
  cancelOnlineOrderLoadingState: LoadingStates.LoadedFail,
  isCancelOnlineOrderFailed: true,
})

const resetCancelOnlineOrder = (state): PurchasesState => ({
  ...state,
  cancelOnlineOrderLoadingState: LoadingStates.NotLoaded,
})

const updateCancelledOnlineOrder = (state, orderId): PurchasesState => ({
  ...state,
  onlineOrders: state.onlineOrders.map((onlineOrder) =>
    onlineOrder.orderId === orderId
      ? {
          ...onlineOrder,
          isCancelled: true,
          statusDisplay: getLabelFromStorage('profile.order.status.cancelled'),
        }
      : onlineOrder,
  ),
  hubOrderList: state.hubOrderList.map((hubOrder) =>
    hubOrder.orderId === orderId
      ? {
          ...hubOrder,
          isCancelled: true,
          statusDisplay: getLabelFromStorage('profile.order.status.cancelled'),
        }
      : hubOrder,
  ),
  onlineOrdersDetails: state.onlineOrdersDetails.map((onlineOrderDetails) =>
    onlineOrderDetails.orderId === orderId
      ? { ...onlineOrderDetails, isCancelled: true }
      : onlineOrderDetails,
  ),
})

// ----------------------------------------
// FETCH STORE ORDERS
// ----------------------------------------
const fetchStoreOrdersStart = (state): PurchasesState => ({
  ...state,
  isStoreOrdersLoading: true,
})

const fetchStoreOrdersSuccess = (state, storeOrders): PurchasesState => ({
  ...state,
  storeOrders: [...state.storeOrders, ...storeOrders],
  isStoreOrdersLoading: false,
  isStoreOrdersPopulated: true,
  fetchStoreIndex: {
    start: state.fetchStoreIndex.start + STORE_ORDERS_LOAD_MORE_AMOUNT,
    end: state.fetchStoreIndex.end + STORE_ORDERS_LOAD_MORE_AMOUNT,
  },
})

const fetchStoreOrdersTimeout = (state): PurchasesState => ({
  ...state,
  isStoreOrdersLoading: false,
})

const fetchStoreOrdersFail = (state, error): PurchasesState => {
  consoleEvents.error('Failed to fetch store orders', error)
  return {
    ...state,
    isStoreOrdersLoading: false,
    isStoreOrdersLoadingFailed: true,
    isHubOrderListPopulated: state.isOnlineOrdersLoadingFailed,
  }
}

// ----------------------------------------
// FETCH ONLINE ORDER DETAILS
// ----------------------------------------
const fetchOnlineOrderDetailsStart = (state): PurchasesState => ({
  ...state,
  isOrderDetailsLoading: true,
  isOrderDetailsLoadingFailed: false,
})

const fetchOnlineOrderDetailsSuccess = (state, onlineOrderDetails): PurchasesState => {
  return {
    ...state,
    isOrderDetailsLoading: false,
    onlineOrdersDetails: [
      ...state.onlineOrdersDetails,
      {
        ...onlineOrderDetails,
      },
    ],
  }
}

const fetchOnlineOrderDetailsFail = (state, { error, orderId }): PurchasesState => {
  consoleEvents.error(`Failed to load online order ${orderId}: ${error}`)
  return {
    ...state,
    isOrderDetailsLoading: false,
    isOrderDetailsLoadingFailed: true,
  }
}

// ----------------------------------------
// FETCH STORE ORDER DETAILS
// ----------------------------------------
const fetchStoreOrderDetailsStart = (state): PurchasesState => ({
  ...state,
  isOrderDetailsLoading: true,
})
const fetchStoreOrderDetailsSuccess = (state, storeOrderDetails): PurchasesState => ({
  ...state,
  isOrderDetailsLoading: false,
  storeOrdersDetails: [
    ...state.storeOrdersDetails,
    {
      ...storeOrderDetails,
    },
  ],
})

const fetchStoreOrderDetailsFail = (state, { error, orderId }): PurchasesState => {
  consoleEvents.error(`Failed to load store order ${orderId}: ${error}`)
  return {
    ...state,
    isOrderDetailsLoading: false,
    isOrderDetailsLoadingFailed: true,
  }
}

// ----------------------------------------
// FETCH STORE ORDERS ADDRESSES
// ----------------------------------------
const fetchStoreOrdersAddressesStart = (state): PurchasesState => ({
  ...state,
  isStoreOrdersAdressesFailed: false,
})

const fetchStoreOrdersAddressesSuccess = (state, addresses): PurchasesState => {
  return {
    ...state,
    storeOrders: state.storeOrders.map((storeOrder) => {
      return { ...storeOrder, storeInfo: addresses[storeOrder.storeId] }
    }),
  }
}

const fetchStoreOrdersAddressesFail = (state, error): PurchasesState => {
  consoleEvents.error('Failed to load store orders addresses', error)

  return {
    ...state,
    isStoreOrdersAdressesFailed: true,
  }
}

// ----------------------------------------
// OTHER
// ----------------------------------------

const setNoMoreOnlineResults = (state): PurchasesState => ({
  ...state,
  noMoreOnlineResults: true,
})

const setNoMoreStoreResults = (state): PurchasesState => ({
  ...state,
  noMoreStoreResults: true,
})

const setHubOrderList = (state, orders): PurchasesState => ({
  ...state,
  hubOrderList: orders,
  isHubOrderListPopulated: true,
})

const setSelectedTab = (state, tab): PurchasesState => ({
  ...state,
  selectedTab: tab,
})

const setBackToOrderUrl = (state, backToOrderUrl): PurchasesState => ({
  ...state,
  backToOrderUrl,
})

const setCodReturnUrl = (state, codReturnUrl): PurchasesState => ({
  ...state,
  codReturnUrl,
})

const setTotalOrdersCount = (state: PurchasesState, { type, count }): PurchasesState => ({
  ...state,
  totalOrdersCount: {
    ...state.totalOrdersCount,
    [type]: count,
  },
})

const setCallRateAndReviewAPIStatus = (
  state: PurchasesState,
  callRateAndReviewAPI,
): PurchasesState => ({
  ...state,
  shouldCallRateAndReviewAPI: callRateAndReviewAPI,
})

const addToBagStart = (state): PurchasesState => ({
  ...state,
  isAddtoBagLoading: true,
})

const addToBagSuccess = (state, { response }): PurchasesState => ({
  ...state,
  response,
  isAddtoBagLoading: false,
})

const addToBagFail = (state): PurchasesState => {
  return {
    ...state,
    isAddtoBagLoading: false,
    isAddtoBagLoadingFailed: true,
  }
}

const showAddToBagVerificationStart = (state): PurchasesState => ({
  ...state,
  addToBagVerificationItem: {},
  shouldShowVerification: false,
  addToBagVerificationError: {
    hasError: false,
    errorMessage: '',
  },
})

const showAddToBagVerificationSuccess = (state, { productData }): PurchasesState => ({
  ...state,
  addToBagVerificationItem: { ...productData },
  shouldShowVerification: true,
})

const showAddToBagVerificationError = (state): PurchasesState => {
  return {
    ...state,
    shouldShowVerification: true,
    addToBagVerificationError: {
      hasError: true,
      errorMessage: getLabelFromStorage('fashionnews.customer.generic.issue'),
    },
  }
}

const setShouldShowAddToBagVerification = (state, { response }): PurchasesState => ({
  ...state,
  shouldShowVerification: response,
})

const reducer = createReducer((on) => {
  on(actions.fetchOnlineOrdersStart, fetchOnlineOrdersStart)
  on(actions.fetchOnlineOrdersSuccess, fetchOnlineOrdersSuccess)
  on(actions.fetchOnlineOrdersFail, fetchOnlineOrdersFail)

  on(actions.setUserHasOrderRecommendations, setUserHasOrderRecommendations)
  on(actions.saveSYLArticles, saveSYLArticles)

  on(actions.fetchStoreOrdersStart, fetchStoreOrdersStart)
  on(actions.fetchStoreOrdersSuccess, fetchStoreOrdersSuccess)
  on(actions.fetchStoreOrdersTimeout, fetchStoreOrdersTimeout)
  on(actions.fetchStoreOrdersFail, fetchStoreOrdersFail)

  on(actions.fetchOnlineActiveOrdersStart, fetchOnlineActiveOrdersStart)
  on(actions.fetchOnlineActiveOrdersSuccess, fetchOnlineActiveOrdersSuccess)
  on(actions.fetchOnlineActiveOrdersFail, fetchOnlineActiveOrdersFail)

  on(actions.fetchOnlineOrderDetailsStart, fetchOnlineOrderDetailsStart)
  on(actions.fetchOnlineOrderDetailsSuccess, fetchOnlineOrderDetailsSuccess)
  on(actions.fetchOnlineOrderDetailsFail, fetchOnlineOrderDetailsFail)
  on(actions.cancelOnlineOrderStart, cancelOnlineOrderStart)
  on(actions.cancelOnlineOrderSuccess, cancelOnlineOrderSuccess)
  on(actions.cancelOnlineOrderFail, cancelOnlineOrderFail)
  on(actions.resetCancelOnlineOrder, resetCancelOnlineOrder)
  on(actions.updateCancelledOnlineOrder, updateCancelledOnlineOrder)

  on(actions.fetchStoreOrderDetailsStart, fetchStoreOrderDetailsStart)
  on(actions.fetchStoreOrderDetailsSuccess, fetchStoreOrderDetailsSuccess)
  on(actions.fetchStoreOrderDetailsFail, fetchStoreOrderDetailsFail)

  on(actions.fetchStoreOrdersAddressesStart, fetchStoreOrdersAddressesStart)
  on(actions.fetchStoreOrdersAddressesSuccess, fetchStoreOrdersAddressesSuccess)
  on(actions.fetchStoreOrdersAddressesFail, fetchStoreOrdersAddressesFail)

  on(actions.setNoMoreOnlineResults, setNoMoreOnlineResults)
  on(actions.setNoMoreStoreResults, setNoMoreStoreResults)
  on(actions.setHubOrderList, setHubOrderList)
  on(actions.setSelectedTab, setSelectedTab)
  on(actions.setBackToOrderUrl, setBackToOrderUrl)
  on(actions.setCodReturnUrl, setCodReturnUrl)
  on(actions.setTotalOrdersCount, setTotalOrdersCount)
  on(actions.setCallRateAndReviewAPIStatus, setCallRateAndReviewAPIStatus)

  on(actions.addToBagStart, addToBagStart)
  on(actions.addToBagSuccess, addToBagSuccess)
  on(actions.showAddToBagVerificationStart, showAddToBagVerificationStart)
  on(actions.showAddToBagVerificationSuccess, showAddToBagVerificationSuccess)
  on(actions.addToBagFail, addToBagFail)
  on(actions.showAddToBagVerificationError, showAddToBagVerificationError)
  on(actions.setShouldShowAddToBagVerification, setShouldShowAddToBagVerification)
}, initialState)

export default reducer
